// page-backlog-review-board.ts — Vue Tableau de la revue (glisser-déposer).
//
// Second rendu de la MÊME liste : aucun endpoint nouveau, aucun état nouveau.
// Quatre colonnes de piles, et une bande « ⚑ à trancher » au-dessus — parce que
// la pile et le drapeau sont deux axes indépendants, et qu'une vue qui les
// mélangerait ferait croire à une cinquième pile.
//
// Trois règles de cette vue, et elles ne sont pas négociables :
//
// 1. **Le glisser-déposer DOUBLE les boutons, il ne les remplace jamais.** Tout
//    ce qui se fait à la souris ici se fait au clavier depuis la vue Liste, qui
//    reste le chemin de référence. Cette vue n'introduit aucun geste exclusif.
// 2. **Un dépôt n'est jamais une écriture.** Déposer sur « Appliquée » ouvre
//    l'écran métier, déposer sur « Rejetée » ou « Reportée » demande le motif ou
//    l'échéance. Si l'utilisateur annule, rien n'a bougé.
// 3. **La carte ne se déplace pas d'elle-même.** On ne joue aucune animation
//    optimiste : la colonne ne change qu'après confirmation du serveur. Une
//    carte qui saute puis revient est pire qu'une carte qui attend.

export interface BoardReco {
  id: number;
  family: string;
  targets: Array<{ type: string; id: number }>;
  title: string;
  pile: string;
  to_arbitrate: boolean;
  unavailable?: boolean;
  annotation?: string | null;
  /** L'argument qui motive la proposition (« échéance du 12/03 dépassée »). */
  rationale?: string | null;
  /** Qui l'a produite, et avec quelle confiance — « Règles · confiance élevée ». */
  provenance?: string | null;
  confidence?: string | null;
}

export interface BoardHandlers {
  /** Demande de changement de pile — la modale de motif ou l'écran métier est
   *  du ressort de l'appelant, qui possède déjà ces gestes pour la vue Liste. */
  requestPile(reco: BoardReco, pile: string): void;
  setFlag(reco: BoardReco, value: boolean): void;
  annotate(reco: BoardReco): void;
  familyLabel(family: string): string;
  pileLabel(pile: string): string;
  /** « Règles · confiance élevée », déjà traduit par l'écran de revue. */
  ruleLabel(reco: BoardReco): string;
  /** Les pastilles vitalité / priorité de l'action visée, déjà rendues.
   *  Le calcul vit dans l'écran de revue, qui possède le constat figé. */
  profileBadges(reco: BoardReco): string;
}

export const BOARD_PILES = ['todo', 'applied', 'rejected', 'snoozed'] as const;

/** Les mêmes gestes que la vue Liste, sur la carte.
 *
 * Règle 1 du module : le glisser-déposer DOUBLE les boutons, il ne les remplace
 * pas. Une carte sans boutons obligerait à la souris — donc à repasser en vue
 * Liste pour tout arbitrage au clavier, ce qui vide la vue Tableau de son
 * intérêt. `draggable="false"` sur les boutons : sans lui, un appui long sur un
 * bouton démarre un glisser au lieu de déclencher le clic.
 */
function _cardActions(reco: BoardReco): string {
  if (reco.pile === 'applied') return '';
  const disabled = reco.unavailable ? ' disabled' : '';
  return `<div class="flex gap-2" style="flex-wrap:wrap;margin-top:6px">
      <button class="btn btn-primary btn-sm" draggable="false"
        data-board-pile="applied" data-id="${reco.id}"${disabled}>${esc(t('review.act.apply'))}</button>
      <button class="btn btn-secondary btn-sm" draggable="false"
        data-board-pile="rejected" data-id="${reco.id}">${esc(t('review.act.reject'))}</button>
      <button class="btn btn-secondary btn-sm" draggable="false"
        data-board-pile="snoozed" data-id="${reco.id}">${esc(t('review.act.later'))}</button>
    </div>`;
}

// Cartes dont le détail est déplié, par identifiant. L'état vit ici et non dans
// l'écran de revue : c'est une préférence de lecture de CETTE vue, elle n'a pas
// à survivre à un changement de page ni à voyager dans le hash.
const _expanded = new Set<number>();

/**
 * L'argument, la règle et l'annotation — repliés par défaut.
 *
 * Une colonne de tableau est étroite : les afficher en permanence réduirait
 * chaque carte à un pavé de texte et ferait perdre la vue d'ensemble, qui est
 * la raison d'être de cette vue. Ils restent à un clic, sur la carte, sans
 * repasser par la liste.
 */
function _cardDetails(reco: BoardReco, handlers: BoardHandlers): string {
  const parts: string[] = [];
  if (reco.rationale) parts.push(`<div class="text-xs">${esc(reco.rationale)}</div>`);
  const rule = handlers.ruleLabel(reco);
  if (rule) parts.push(`<div class="text-xs text-muted">${esc(rule)}</div>`);
  if (reco.annotation) parts.push(`<div class="text-xs">✎ ${esc(reco.annotation)}</div>`);
  if (!parts.length) return '';
  const open = _expanded.has(reco.id);
  return `<div style="margin-top:4px">
      <button type="button" draggable="false" data-board-details="${reco.id}"
        aria-expanded="${open}" aria-controls="board-details-${reco.id}"
        title="${esc(open ? t('review.board.hideDetails') : t('review.board.showDetails'))}"
        style="display:flex;align-items:center;gap:4px;background:none;border:none;cursor:pointer;padding:0;font:inherit;color:var(--text2)">
        <span style="font-size:10px;display:inline-block;transition:transform .15s;transform:rotate(${open ? '0deg' : '-90deg'})">v</span>
        <span class="text-xs">${esc(open ? t('review.board.hideDetails') : t('review.board.showDetails'))}</span>
      </button>
      <div id="board-details-${reco.id}" style="${open ? '' : 'display:none'};margin-top:4px">${parts.join('')}</div>
    </div>`;
}

function _cardHtml(reco: BoardReco, handlers: BoardHandlers): string {
  const codes = reco.targets
    .filter((tg) => tg.type === 'action')
    .map((tg) => `#${tg.id}`)
    .join(' + ');
  const caduque = reco.unavailable ? ' ⚠' : '';
  return `<div class="card" draggable="true" data-card="${reco.id}"
      style="padding:8px;margin-bottom:6px;cursor:grab">
      <div class="flex gap-2" style="align-items:baseline;flex-wrap:wrap">
        <span class="badge">${esc(handlers.familyLabel(reco.family))}</span>
        ${reco.to_arbitrate ? `<span class="badge badge-warning">⚑ ${esc(t('review.chip.toArbitrate'))}</span>` : ''}
        ${handlers.profileBadges(reco)}
        <span class="text-mono text-xs">${esc(codes)}${caduque}</span>
        <span style="margin-left:auto" class="flex gap-2">
          <button class="btn btn-secondary btn-sm" draggable="false"
            data-board-flag="${reco.id}" aria-pressed="${reco.to_arbitrate}"
            aria-label="${esc(reco.to_arbitrate ? t('review.flag.remove') : t('review.flag.set'))}"
            title="${esc(reco.to_arbitrate ? t('review.flag.remove') : t('review.flag.set'))}">⚑</button>
          <button class="btn btn-secondary btn-sm" draggable="false" style="color:#147A57"
            data-board-note="${reco.id}"
            aria-label="${esc(t('review.annotate'))}" title="${esc(t('review.annotate'))}">✎</button>
        </span>
      </div>
      <div style="font-size:var(--fs-sm);margin-top:4px">${esc(reco.title)}</div>
      ${_cardDetails(reco, handlers)}
      ${_cardActions(reco)}
    </div>`;
}

function _columnHtml(pile: string, rows: BoardReco[], handlers: BoardHandlers): string {
  const cards = rows.map((r) => _cardHtml(r, handlers)).join('');
  return `<div class="card" data-drop-pile="${pile}" style="padding:10px;min-height:120px">
      <div class="card-title" style="font-size:var(--fs-sm);margin-bottom:8px">
        ${esc(handlers.pileLabel(pile))} <span class="text-muted">${rows.length}</span>
      </div>
      ${cards || `<div class="empty" style="padding:8px">—</div>`}
    </div>`;
}

export function renderBoard(recos: BoardReco[], handlers: BoardHandlers): string {
  const flagged = recos.filter((r) => r.to_arbitrate);
  const chips = flagged
    .map(
      (r) => `<span class="tag-chip">⚑ ${esc(handlers.familyLabel(r.family))} ${esc(r.title)}
        <button class="tag-remove" data-unflag="${r.id}"
          aria-label="${esc(t('review.flag.remove'))}" title="${esc(t('review.flag.remove'))}">✕</button>
      </span>`,
    )
    .join(' ');
  const columns = BOARD_PILES.map((p) =>
    _columnHtml(
      p,
      recos.filter((r) => r.pile === p),
      handlers,
    ),
  ).join('');
  return `<div data-board style="margin-top:8px">
      <div class="card" data-drop-flag style="padding:10px;margin-bottom:10px;border-style:dashed">
        <div class="card-title" style="font-size:var(--fs-sm)">
          ⚑ ${esc(t('review.board.arbitrateBand'))}
          <span class="text-muted text-xs">— ${esc(t('review.board.dropHere'))}</span>
        </div>
        <div style="margin-top:6px">${chips || `<span class="text-muted text-xs">${esc(t('review.board.noneFlagged'))}</span>`}</div>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:10px">
        ${columns}
      </div>
    </div>`;
}

export function bindBoard(recos: BoardReco[], handlers: BoardHandlers): void {
  const byId = new Map(recos.map((r) => [r.id, r]));
  let dragged: BoardReco | null = null;

  document.querySelectorAll<HTMLElement>('[data-card]').forEach((card) => {
    card.addEventListener('dragstart', (e) => {
      dragged = byId.get(Number(card.dataset.card)) ?? null;
      (e as DragEvent).dataTransfer?.setData('text/plain', card.dataset.card || '');
    });
    card.addEventListener('dragend', () => {
      dragged = null;
    });
  });

  const allowDrop = (zone: HTMLElement): void => {
    zone.addEventListener('dragover', (e) => e.preventDefault());
  };

  document.querySelectorAll<HTMLElement>('[data-drop-pile]').forEach((zone) => {
    allowDrop(zone);
    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      const reco = dragged;
      dragged = null;
      if (!reco) return;
      const target = zone.dataset.dropPile || '';
      if (target === reco.pile) return;
      // Une application est un fait métier : elle se défait dans l'écran de
      // l'action, pas en tirant une carte hors de sa colonne.
      if (reco.pile === 'applied') {
        toast(t('review.board.appliedIsFinal'), 'error');
        return;
      }
      handlers.requestPile(reco, target);
    });
  });

  const band = document.querySelector<HTMLElement>('[data-drop-flag]');
  if (band) {
    allowDrop(band);
    band.addEventListener('drop', (e) => {
      e.preventDefault();
      const reco = dragged;
      dragged = null;
      // Déposer sur la bande POSE le drapeau sans toucher à la pile : c'est
      // tout l'intérêt de la bande, et la raison pour laquelle elle n'est pas
      // une cinquième colonne.
      if (reco && !reco.to_arbitrate) handlers.setFlag(reco, true);
    });
  }

  document.querySelectorAll<HTMLElement>('[data-unflag]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const reco = byId.get(Number(btn.dataset.unflag));
      if (reco) handlers.setFlag(reco, false);
    });
  });

  // Les boutons de la carte. Même garde que le dépôt : une appliquée ne se
  // défait pas d'ici (elle n'affiche d'ailleurs aucun bouton).
  document.querySelectorAll<HTMLElement>('[data-board-pile]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const reco = byId.get(Number(btn.dataset.id));
      const target = btn.dataset.boardPile || '';
      if (reco && target !== reco.pile) handlers.requestPile(reco, target);
    });
  });

  document.querySelectorAll<HTMLElement>('[data-board-flag]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const reco = byId.get(Number(btn.dataset.boardFlag));
      if (reco) handlers.setFlag(reco, !reco.to_arbitrate);
    });
  });

  document.querySelectorAll<HTMLElement>('[data-board-note]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const reco = byId.get(Number(btn.dataset.boardNote));
      if (reco) handlers.annotate(reco);
    });
  });

  // Plier / déplier le détail : on bascule le DOM sur place plutôt que de
  // re-rendre tout le tableau — un re-rendu perdrait le défilement des colonnes
  // et le focus clavier sur la carte qu'on vient d'ouvrir.
  document.querySelectorAll<HTMLElement>('[data-board-details]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = Number(btn.dataset.boardDetails);
      const body = document.getElementById(`board-details-${id}`);
      if (!body) return;
      const open = !_expanded.has(id);
      if (open) _expanded.add(id);
      else _expanded.delete(id);
      body.style.display = open ? '' : 'none';
      btn.setAttribute('aria-expanded', String(open));
      const label = open ? t('review.board.hideDetails') : t('review.board.showDetails');
      btn.setAttribute('title', label);
      const arrow = btn.querySelector<HTMLElement>('span');
      if (arrow) arrow.style.transform = `rotate(${open ? '0deg' : '-90deg'})`;
      const text = btn.querySelectorAll<HTMLElement>('span')[1];
      if (text) text.textContent = label;
    });
  });
}
